import { BALANCE as B, PREFERRED_ROLE, rank } from "./data";
import { clamp, clone, random } from "./rng";
import type {
  Admiral,
  BattleState,
  CombatEvent,
  CombatSide,
  CombatUnit,
  FactionId,
  FactionState,
  Ship,
  ShipClass,
  Stance,
  Difficulty,
} from "./types";
export function shipMax(type: ShipClass, f: FactionId, tech = 0) {
  const b = B.ships[type],
    m = 1 + B.technology.shieldsAndHullBonus[tech];
  return {
    hull: Math.round(
      b.hull * m * (f === "nocturn" && type === "dreadnought" ? 1.15 : 1),
    ),
    shields: Math.round(b.shields * m * (f === "okalani" ? 1.15 : 1)),
  };
}
export function admiralBonuses(a: Admiral | null, f: FactionId) {
  if (!a) return { attack: 0, resistance: 0 };
  const r = rank(a.xp),
    p = PREFERRED_ROLE[f] === a.role ? 0.03 : 0;
  return {
    attack:
      a.role === "vanguard"
        ? B.admirals.attackBonusByRank[r] + p
        : a.role === "navigator"
          ? B.admirals.navigatorBothByRank[r] + p
          : 0,
    resistance:
      a.role === "guardian"
        ? B.admirals.guardianResistanceByRank[r] + p
        : a.role === "navigator"
          ? B.admirals.navigatorBothByRank[r] + p
          : 0,
  };
}
export function combatShip(
  s: Ship,
  f: FactionId,
  fs: FactionState | null,
  a: Admiral | null,
): CombatUnit {
  const b = B.ships[s.type],
    tech = fs?.tech,
    m = shipMax(s.type, f, tech?.shields ?? 0),
    ab = admiralBonuses(a, f);
  return {
    id: s.id,
    type: s.type,
    faction: f,
    hull: clamp(s.hull, 0, m.hull),
    shields: clamp(s.shields, 0, m.shields),
    maxHull: m.hull,
    maxShields: m.shields,
    attack: Math.round(
      b.attack *
        (f === "reptiloids" ? B.factions.reptiloids.modifiers.attack : 1) *
        (1 + B.technology.weaponsBonus[tech?.weapons ?? 0]) *
        (1 + ab.attack) *
        (1 - (fs?.shortage ?? 0) * 0.1),
    ),
    accuracy: b.accuracy,
    accuracyBonus: B.technology.propulsionAccuracyBonus[tech?.propulsion ?? 0],
    evasion: b.evasion * (f === "greys" && s.type === "scout" ? 1.1 : 1),
    initiative: b.initiative,
    resistance: ab.resistance,
  };
}
export function combatBastion(
  id: string,
  f: FactionId,
  level: number,
  grid: number,
): CombatUnit {
  const h = Math.round(B.bastion.hull[level] * (f === "xilur" ? 1.15 : 1)),
    s = B.bastion.shields[level];
  return {
    id,
    type: "bastion",
    faction: f,
    hull: h,
    shields: s,
    maxHull: h,
    maxShields: s,
    attack: Math.round(B.bastion.attack[level] * (1 + grid * 0.1)),
    accuracy: 0.88,
    accuracyBonus: 0,
    evasion: 0,
    initiative: 2,
    resistance: grid * 0.1,
  };
}
export function unitValue(u: CombatUnit) {
  return u.type === "bastion" ? 160 : B.ships[u.type].cost;
}
export function effectivePower(us: CombatUnit[]) {
  return us
    .filter((u) => u.hull > 0)
    .reduce(
      (v, u) =>
        v + Math.sqrt(u.hull + u.shields) * u.attack * (1 - u.resistance * 0.4),
      0,
    );
}
/** Weighted roulette: offensive=threat/HP; neutral=1/HP; defensive=threat*price*remaining HP fraction. One draw per target. */
export function targetWeight(u: CombatUnit, s: Stance) {
  const hp = Math.max(1, u.hull + u.shields);
  return s === "offensive"
    ? Math.max(1, u.attack) / hp
    : s === "neutral"
      ? 1 / hp
      : (Math.max(1, u.attack) * unitValue(u) * hp) /
        (u.maxHull + u.maxShields);
}
export function aiStance(
  s: CombatSide,
  e: CombatSide,
  d: Difficulty = "normal",
): Stance {
  const p = effectivePower(s.units),
    v = effectivePower(e.units);
  if (d === "easy") return p > v * 1.5 ? "neutral" : "defensive";
  return p > v * 1.3
    ? "offensive"
    : p < v * 0.9 ||
        s.units.some((u) => u.hull > 0 && u.hull < u.maxHull * 0.35)
      ? "defensive"
      : "neutral";
}
function strike(
  b: BattleState,
  u: CombatUnit,
  s: CombatSide,
  e: CombatSide,
  mult: number,
  events: CombatEvent[],
) {
  const ts = e.units.filter((x) => x.hull > 0);
  if (u.hull <= 0 || !ts.length) return;
  const ws = ts.map((x) => targetWeight(x, s.stance));
  let roll = random(b) * ws.reduce((a, c) => a + c, 0),
    target = ts[ts.length - 1];
  for (let i = 0; i < ts.length; i++) {
    roll -= ws[i];
    if (roll <= 0) {
      target = ts[i];
      break;
    }
  }
  const hit = clamp(
    u.accuracy * (1 - target.evasion) + u.accuracyBonus,
    0.55,
    0.98,
  );
  if (random(b) > hit) {
    events.push({
      round: b.round,
      type: "miss",
      source: u.id,
      target: target.id,
      damage: 0,
      shields: target.shields,
      hull: target.hull,
    });
    return;
  }
  const first =
    b.nebula && u.faction === "nocturn" && !b.nocturnTriggered ? 1.12 : 1;
  if (first > 1) b.nocturnTriggered = true;
  const damage = Math.max(
      1,
      Math.round(
        u.attack *
          B.stances[s.stance].outgoingAttack *
          mult *
          first *
          (0.85 + 0.3 * random(b)) *
          B.stances[e.stance].incomingDamage *
          (1 - target.resistance),
      ),
    ),
    absorbed = Math.min(damage, target.shields);
  target.shields -= absorbed;
  target.hull = Math.max(0, target.hull - damage + absorbed);
  events.push({
    round: b.round,
    type: "hit",
    source: u.id,
    target: target.id,
    damage,
    shields: target.shields,
    hull: target.hull,
  });
  if (!target.hull)
    events.push({
      round: b.round,
      type: "destroy",
      source: u.id,
      target: target.id,
      damage: 0,
      shields: 0,
      hull: 0,
    });
}
export function simulateRound(
  state: BattleState,
  stance: Stance,
  rngOrSide: { rng: number } | "attacker" | "defender" = "attacker",
  explicitSide: "attacker" | "defender" = "attacker",
): { newState: BattleState; events: CombatEvent[] } {
  const b = clone(state),
    events: CombatEvent[] = [],
    side = typeof rngOrSide === "string" ? rngOrSide : explicitSide;
  if (typeof rngOrSide !== "string") b.rng = rngOrSide.rng >>> 0;
  if (b.status !== "active") return { newState: b, events };
  b[side].stance = stance;
  const other = side === "attacker" ? "defender" : "attacker";
  b[other].stance = aiStance(b[other], b[side], b.difficulty);
  b.round++;
  const actors = [
    ...b.attacker.units.map((u) => ({
      u,
      s: b.attacker,
      e: b.defender,
      tie: random(b),
    })),
    ...b.defender.units.map((u) => ({
      u,
      s: b.defender,
      e: b.attacker,
      tie: random(b),
    })),
  ]
    .filter((x) => x.u.hull > 0)
    .sort(
      (a, c) =>
        c.u.initiative - a.u.initiative ||
        a.tie - c.tie ||
        (a.u.id < c.u.id ? -1 : 1),
    );
  for (const a of actors) {
    strike(b, a.u, a.s, a.e, 1, events);
    if (
      !b.attacker.units.some((u) => u.hull > 0) ||
      !b.defender.units.some((u) => u.hull > 0)
    )
      break;
  }
  if (!b.attacker.units.some((u) => u.hull > 0)) b.status = "defender";
  else if (!b.defender.units.some((u) => u.hull > 0)) b.status = "attacker";
  else if (b.round >= 24) b.status = "defender";
  if (b.status !== "active")
    events.push({
      round: b.round,
      type: "end",
      source: "",
      target: "",
      damage: 0,
      shields: 0,
      hull: 0,
    });
  b.events.push(...events);
  return { newState: b, events };
}
export function retreatSalvo(state: BattleState) {
  const b = clone(state),
    events: CombatEvent[] = [];
  if (b.status !== "active") return { newState: b, events };
  for (const u of b.defender.units
    .filter((u) => u.hull > 0)
    .sort((a, c) => c.initiative - a.initiative || (a.id < c.id ? -1 : 1)))
    strike(b, u, b.defender, b.attacker, 0.5, events);
  b.status = b.attacker.units.some((u) => u.hull > 0) ? "retreat" : "defender";
  events.push({
    round: b.round,
    type: "retreat",
    source: "",
    target: "",
    damage: 0,
    shields: 0,
    hull: 0,
  });
  b.events.push(...events);
  return { newState: b, events };
}
